import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { CollaboratorAccrualsRepositoryT } from "../domain/ports/collaboratorAccruals.repository";
import type { AccrualsRepositoryT } from "../domain/ports/accruals.repository";
import type { CollaboratorAccrualEntityT } from "../domain/entities/collaboratorAccruals.entity";
import type { CollaboratorAccrualsUiT } from "../domain/ui/collaboratorAccruals.model";

import type { UpdateAccrualInputT } from "../domain/types/collaboratorAccruals.types";
import type { AccrualRowT } from "../domain/types/accrual.types";

import {
  displayToIsoDate,
  isoToDisplayDate,
} from "../helpers/accrualDateFormat";

import { getCollaboratorAccruals } from "../application/useCases/getCollaboratorAccruals";
import { deleteCollaboratorAccruals } from "../application/useCases/deleteCollaboratorAccruals";
import { updateCollaboratorAccruals } from "../application/useCases/updateCollaboratorAccruals";
import { createAccrual } from "../application/useCases/createAccrual";
import {
  BALANCES_QUERY_KEY,
  COLLABORATOR_ACCRUALS_QUERY_KEY,
} from "../constants/queryKeys";

import { NEGATIVE_ACCRUAL_TYPE } from "../constants/accrualType";

const isNegative = (accrualType: string) =>
  accrualType === NEGATIVE_ACCRUAL_TYPE;

const toUiModel = (
  data: CollaboratorAccrualEntityT,
): CollaboratorAccrualsUiT => {
  return {
    accruals: (data.accruals ?? []).map((accrual) => {
      return {
        id: accrual.id,
        accrualType: accrual.accrualType,
        isInitialBalance: accrual.isInitialBalance,
        registrationDate: isoToDisplayDate(accrual.registrationDate),
        registrationDateValue: accrual.registrationDate
          ? new Date(accrual.registrationDate).getTime()
          : null,
        description: accrual.description ?? "-",
        vacationDays: isNegative(accrual.accrualType)
          ? 0
          : accrual.vacationDays,
        vacationTaken: isNegative(accrual.accrualType)
          ? accrual.vacationDays
          : 0,

        // TODO: descomentar para reactivar.  % prima y días prima ya no vienen en la plantilla
        primaDays: 0, // accrual.primaDays,
        primaPercentage: 0, // accrual.primaPercentage,
      };
    }),
    vacationSourceStart: data.vacationSourceStart,
  };
};

const removeDeletedAccruals = (
  oldData: CollaboratorAccrualEntityT | undefined,
  succeededIds: Set<string>,
) => {
  if (!oldData) return oldData;

  return {
    ...oldData,
    total: Math.max(oldData.total - succeededIds.size, 0),
    accruals: (oldData.accruals ?? []).filter(
      (accrual) => !succeededIds.has(accrual.id),
    ),
  };
};

const applyUpdatedAccruals = (
  oldData: CollaboratorAccrualEntityT | undefined,
  succeeded: UpdateAccrualInputT[],
) => {
  if (!oldData) return oldData;

  const succeededById = new Map(
    succeeded.map((accrual) => [accrual.id, accrual]),
  );

  return {
    ...oldData,
    accruals: (oldData.accruals ?? []).map((accrual) => {
      const update = succeededById.get(accrual.id);
      if (!update) return accrual;

      return {
        ...accrual,
        description: update.description,
        isInitialBalance: update.isInitialBalance,
        primaDays: update.primaDays,
        primaPercentage: update.primaPercentage,
        registrationDate: displayToIsoDate(update.registrationDate),
        vacationDays: update.vacationDays,
      };
    }),
  };
};

type CreateUseCollaboratorAccrualsParamsT = {
  collaboratorAccrualsRepository: CollaboratorAccrualsRepositoryT;
  accrualsRepository: AccrualsRepositoryT;
};

export const createUseCollaboratorAccruals = ({
  collaboratorAccrualsRepository,
  accrualsRepository,
}: CreateUseCollaboratorAccrualsParamsT) => {
  return function useCollaboratorAccruals({
    collaboratorId,
    colaboraInstanceId,
  }: {
    collaboratorId?: string;
    colaboraInstanceId?: string;
  }) {
    const queryClient = useQueryClient();

    const queryKey = [
      COLLABORATOR_ACCRUALS_QUERY_KEY,
      { collaboratorId, colaboraInstanceId },
    ];

    const collaboratorAccrualsQuery = useQuery({
      queryKey,
      queryFn: () =>
        getCollaboratorAccruals({
          collaboratorId,
          colaboraInstanceId,
          collaboratorAccrualsRepository,
        }),
      select: toUiModel,
      enabled: !!collaboratorId && !!colaboraInstanceId,
    });

    const deleteAccrualsMutation = useMutation({
      mutationFn: ({ accrualIds }: { accrualIds: string[] }) =>
        deleteCollaboratorAccruals({
          accrualIds,
          colaboraInstanceId,
          collaboratorAccrualsRepository,
        }),
      onSuccess: (result, { accrualIds }) => {
        const failedIds = new Set(result.errors.map((item) => item.accrualId));
        const succeededIds = new Set(
          accrualIds.filter((id) => !failedIds.has(id)),
        );

        if (succeededIds.size === 0) return;

        queryClient.setQueryData(
          queryKey,
          (oldData: CollaboratorAccrualEntityT | undefined) =>
            removeDeletedAccruals(oldData, succeededIds),
        );

        queryClient.invalidateQueries({ queryKey: [BALANCES_QUERY_KEY] });
      },
    });

    const updateAccrualsMutation = useMutation({
      mutationFn: ({ accruals }: { accruals: UpdateAccrualInputT[] }) =>
        updateCollaboratorAccruals({
          accruals,
          colaboraInstanceId,
          collaboratorAccrualsRepository,
        }),
      onSuccess: (result, { accruals }) => {
        const failedIds = new Set(result.errors.map((item) => item.accrualId));
        const succeeded = accruals.filter(
          (accrual) => !failedIds.has(accrual.id),
        );

        if (succeeded.length === 0) return;

        queryClient.setQueryData(
          queryKey,
          (oldData: CollaboratorAccrualEntityT | undefined) =>
            applyUpdatedAccruals(oldData, succeeded),
        );

        queryClient.invalidateQueries({ queryKey: [BALANCES_QUERY_KEY] });
      },
    });

    const createAccrualMutation = useMutation({
      mutationFn: ({ rows }: { rows: AccrualRowT[] }) =>
        createAccrual({
          accrualsRepository,
          rows,
          colaboraInstanceId,
        }),
      onSuccess: (result) => {
        if (result.processed === 0) return;

        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries({ queryKey: [BALANCES_QUERY_KEY] });
      },
    });

    return {
      ...collaboratorAccrualsQuery,
      deleteAccruals: deleteAccrualsMutation,
      updateAccruals: updateAccrualsMutation,
      createAccrual: createAccrualMutation,
    };
  };
};
